// src/admin/components/payments/PaymentList.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  RiMoneyDollarCircleLine,
  RiAddLine,
  RiFilter2Line,
  RiSearchLine,
  RiPencilLine,
  RiDeleteBin6Line,
  RiEyeLine,
  RiAlertLine,
  RiRefreshLine,
  RiCalendarLine,
  RiUserLine,
  RiFileTextLine,
  RiPrinterLine,
  RiBuildingLine,
  RiCloseLine,
  RiDownloadLine,
  RiBarChartBoxLine,
  RiFilterOffLine,
  RiArrowRightLine
} from 'react-icons/ri';
import {
  getAllPayments,
  deletePayment,
  formatCurrency,
  formatPaymentDate,
  getPaymentTypeColor,
  PAYMENT_TYPE_OPTIONS,
  PAYMENT_PERIOD_OPTIONS
} from '../../../models/paymentModel';
import { useTheme } from '../../../contexts/ThemeContext';
import { getCurrentUser, isAuthenticated } from '../../../models/authModel';
import PrintReceipt from './PrintReceipt';
import i18n from '../../../i18n';

const PaymentList = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    tenantId: '',
    paymentType: '',
    buildingId: '',
    fromDate: '',
    toDate: '',
    searchTerm: ''
  });

  // Stats state
  const [showStats, setShowStats] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    paymentId: null,
    amount: '',
    tenantName: ''
  });

  // Print receipt state
  const [showReceipt, setShowReceipt] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [deleting, setDeleting] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  // Check if RTL language
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  // Fetch payments data
  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiFilters = {};
      let filterCount = 0;

      // Map frontend filter names to backend filter names
      if (filters.tenantId) {
        apiFilters.tenantId = filters.tenantId;
        filterCount++;
      }
      if (filters.paymentType) {
        apiFilters.paymentType = filters.paymentType;
        filterCount++;
      }
      if (filters.buildingId) {
        apiFilters.buildingId = filters.buildingId;
        filterCount++;
      }
      if (filters.fromDate) {
        apiFilters.fromDate = filters.fromDate;
        filterCount++;
      }
      if (filters.toDate) {
        apiFilters.toDate = filters.toDate;
        filterCount++;
      }

      setActiveFilters(filterCount);

      // Check authentication before making request
      if (!isAuthenticated()) {
        throw new Error(t('authentication_required'));
      }

      const response = await getAllPayments(apiFilters);

      if (response && response.payments) {
        setPayments(response.payments);
      } else {
        setPayments([]);
      }

    } catch (err) {
      console.error(t('error_fetching_payments'), err);

      let errorMessage = t('failed_load_payments');

      if (err.response?.status === 401) {
        errorMessage = t('auth_failed_login_again');
      } else if (err.response?.status === 403) {
        errorMessage = t('access_denied_admin_required');
      } else if (err.response?.status === 500) {
        errorMessage = t('server_error_check_backend');
      } else if (err.message.includes('Network Error') || err.code === 'ERR_NETWORK') {
        errorMessage = t('cannot_connect_server');
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [filters.tenantId, filters.paymentType, filters.buildingId, filters.fromDate, filters.toDate]);

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      tenantId: '',
      paymentType: '',
      buildingId: '',
      fromDate: '',
      toDate: '',
      searchTerm: ''
    });
    setFilterOpen(false);
  };

  // Manual refresh function
  const handleRefresh = () => {
    fetchPayments();
  };

  // Handle search
  const handleSearch = (e) => {
    setFilters({ ...filters, searchTerm: e.target.value });
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle print receipt
  const handlePrintReceipt = (payment) => {
    setSelectedPayment(payment);
    setShowReceipt(true);
  };

  // Filter payments based on search term
  const filteredPayments = payments.filter(payment => {
    const searchTerm = filters.searchTerm.toLowerCase();

    if (!searchTerm) return true;

    return (
      payment.tenantName?.toLowerCase().includes(searchTerm) ||
      payment.apartmentNumber?.toLowerCase().includes(searchTerm) ||
      payment.buildingName?.toLowerCase().includes(searchTerm) ||
      payment.paymentType?.toLowerCase().includes(searchTerm) ||
      (payment.amount && payment.amount.toString().includes(searchTerm))
    );
  });

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle delete confirmation dialog
  const openDeleteDialog = (paymentId, amount, tenantName) => {
    setDeleteDialog({
      isOpen: true,
      paymentId,
      amount,
      tenantName
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({
      isOpen: false,
      paymentId: null,
      amount: '',
      tenantName: ''
    });
  };

  // Handle payment deletion
  const handleDeletePayment = async () => {
    if (!deleteDialog.paymentId) return;

    try {
      setDeleting(true);

      await deletePayment(deleteDialog.paymentId);

      // Remove the payment from the local state
      setPayments(payments.filter(payment => payment.id !== deleteDialog.paymentId));

      // Close dialog
      closeDeleteDialog();

    } catch (err) {
      console.error(t('error_deleting_payment'), err);
      setError(err.message || t('failed_delete_payment'));
    } finally {
      setDeleting(false);
    }
  };

  // Format payment period for display
  const formatPaymentPeriod = (payment) => {
    if (!payment.months) return t('unknown_period');

    try {
      let months = payment.months;

      // Handle stringified JSON if necessary
      if (typeof months === 'string') {
        try {
          months = JSON.parse(months);
        } catch (e) {
          // If not JSON, check if it's a comma-separated string
          if (months.includes(',')) {
            months = months.split(',').map(m => m.trim());
          } else {
            months = [months];
          }
        }
      }

      if (!Array.isArray(months) || months.length === 0) {
        return months ? String(months) : t('unknown_period');
      }

      // Helper to format a month value (either a date string, month name, or month number)
      const formatMonth = (m) => {
        if (!m) return '';

        // If it's a number (e.g. 1 for January)
        if (typeof m === 'number' || (typeof m === 'string' && !isNaN(m) && !m.includes('-'))) {
          const monthIndex = parseInt(m) - 1;
          const date = new Date();
          if (payment.paymentDate) {
            const pDate = new Date(payment.paymentDate);
            date.setFullYear(pDate.getFullYear());
          }
          date.setMonth(monthIndex);
          return new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
            month: 'long',
            year: 'numeric'
          }).format(date);
        }

        // Otherwise assume it's a date string
        return formatPaymentDate(m);
      };

      if (months.length >= 2) {
        return `${formatMonth(months[0])} ${t('to')} ${formatMonth(months[months.length - 1])}`;
      }

      return formatMonth(months[0]);
    } catch (err) {
      console.error('Error formatting payment period:', err);
      return t('invalid_period_data');
    }
  };

  // Calculate statistics
  const totalAmount = payments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
  const rentPayments = payments.filter(p => p.paymentType === 'rent');
  const rentTotal = rentPayments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
  const servicePayments = payments.filter(p => p.paymentType === 'service');
  const serviceTotal = servicePayments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);

  // Additional statistics
  const paymentsByMonth = payments.reduce((acc, payment) => {
    const date = new Date(payment.paymentDate);
    const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

    if (!acc[monthYear]) {
      acc[monthYear] = {
        count: 0,
        amount: 0
      };
    }

    acc[monthYear].count++;
    acc[monthYear].amount += parseFloat(payment.amount || 0);

    return acc;
  }, {});

  // Get unique buildings
  const uniqueBuildings = [...new Set(payments.map(p => p.buildingName).filter(Boolean))];

  return (
    <div className="space-y-4 text-start" dir={isRTL ? 'rtl' : 'ltr'}>

      <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-md overflow-hidden`}>

        {/* Delete Confirmation Dialog */}
        {deleteDialog.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg max-w-md w-full p-6`}>
              <div className="flex items-center mb-4 text-red-500">
                <RiAlertLine size={24} className="mr-2" />
                <h3 className="text-lg font-semibold">{t('confirm_deletion')}</h3>
              </div>

              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                {t('confirm_delete_payment', {
                  amount: formatCurrency(deleteDialog.amount),
                  tenantName: deleteDialog.tenantName
                })}
              </p>

              <div className={`flex justify-end gap-3`}>
                <button
                  onClick={closeDeleteDialog}
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  disabled={deleting}
                >
                  {t('cancel')}
                </button>

                <button
                  onClick={handleDeletePayment}
                  className={`px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center ${deleting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('deleting')}
                    </>
                  ) : (
                    <>
                      <RiDeleteBin6Line className="mr-2" />
                      {t('delete')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Print Receipt Modal */}
        {showReceipt && selectedPayment && (
          <PrintReceipt
            payment={selectedPayment}
            onClose={() => setShowReceipt(false)}
          />
        )}

        {/* Header */}
        <div className={`p-4 ${isDarkMode ? 'border-b border-gray-700 bg-gray-750' : 'border-b border-gray-200 bg-gray-50'}`}>
          <div className={`flex flex-col md:flex-row justify-between items-start md:items-center gap-4`}>
            <div className="flex items-center space-x-4">
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                <RiMoneyDollarCircleLine className={`mr-2 text-xl ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                {t('payments')}
                {!loading && (
                  <span className={`ml-2 text-sm font-normal ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    ({payments.length})
                  </span>
                )}
              </h2>

              <button
                onClick={handleRefresh}
                className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  } transition-colors duration-200 flex items-center`}
                title={t('refresh_payments')}
              >
                <RiRefreshLine size={16} className="mr-1" />
                <span className="text-sm">{t('refresh')}</span>
              </button>

              <button
                onClick={() => setShowStats(!showStats)}
                className={`p-2 rounded-lg ${isDarkMode
                  ? `${showStats ? 'bg-zinc-600 text-white' : 'bg-zinc-800 text-gray-300'} hover:bg-zinc-500`
                  : `${showStats ? 'bg-zinc-200 text-gray-800' : 'bg-gray-200 text-gray-600'} hover:bg-zinc-300`
                  } transition-colors duration-200 flex items-center`}
                title={t('toggle_statistics')}
              >
                <RiBarChartBoxLine size={16} className="mr-1" />
                <span className="text-sm">{t('stats')}</span>
              </button>
            </div>

            <div className={`flex flex-col sm:flex-row gap-2 w-full md:w-auto`}>
              <div className="relative w-full sm:w-64">
                <div className={`absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none text-zinc-400`}>
                  <RiSearchLine size={18} />
                </div>
                <input
                  type="search"
                  className={`block w-full p-2 ps-10 text-sm ${isDarkMode
                    ? 'bg-zinc-800 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                    } border rounded-lg focus:ring-red-500 focus:border-red-500`}
                  placeholder={t('search_payments')}
                  value={filters.searchTerm}
                  onChange={handleSearch}
                />
              </div>

              <button
                onClick={() => setFilterOpen(!filterOpen)}
                className={`px-3 py-2 text-sm rounded-lg flex items-center ${isDarkMode
                  ? `${filterOpen ? 'bg-red-600 text-white' : 'bg-zinc-800 text-gray-300'} hover:bg-red-700 border-gray-600`
                  : `${filterOpen ? 'bg-zinc-100 text-red-700' : 'bg-gray-100 text-gray-700'} hover:bg-zinc-50 border-gray-300`
                  } border relative`}
              >
                {filterOpen ? <RiFilterOffLine className="mr-2" /> : <RiFilter2Line className="mr-2" />}
                {t('filter')}
                {activeFilters > 0 && (
                  <span className={`absolute -top-2 -right-2 flex items-center justify-center w-5 h-5 text-xs font-bold text-white ${isDarkMode ? 'bg-green-500' : 'bg-green-600'} rounded-full`}>
                    {activeFilters}
                  </span>
                )}
              </button>

              <Link
                to="/admin/payments/add"
                className={`px-3 py-2 text-sm rounded-lg flex items-center ${isDarkMode
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
                  } transition-colors duration-200`}
              >
                <RiAddLine className="mr-2" />
                {t('record_payment')}
              </Link>
            </div>
          </div>

          {/* Filter dropdown */}
          {filterOpen && (
            <div className={`mt-4 p-4 ${isDarkMode ? 'bg-zinc-800 border border-gray-600' : 'bg-white border border-gray-200'} rounded-lg shadow-md`}>
              <div className={`flex items-center justify-between mb-4 `}>
                <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('filter_payments')}
                </h3>
                <div className={`flex items-center gap-2`}>
                  <button
                    onClick={resetFilters}
                    className={`px-3 py-1 text-xs rounded-lg flex items-center ${isDarkMode
                      ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <RiCloseLine className="mr-1" size={14} />
                    {t('reset_all')}
                  </button>
                  <button
                    onClick={() => setFilterOpen(false)}
                    className={`p-1 rounded-full ${isDarkMode
                      ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <RiCloseLine size={18} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('payment_type')}
                  </label>
                  <select
                    value={filters.paymentType}
                    onChange={(e) => setFilters({ ...filters, paymentType: e.target.value })}
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-gray-600 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      } border focus:ring-red-500 focus:border-red-500`}
                  >
                    <option value="">{t('all_types')}</option>
                    {PAYMENT_TYPE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {t(`payment_type_${option.value}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('building')}
                  </label>
                  <select
                    value={filters.buildingId}
                    onChange={(e) => setFilters({ ...filters, buildingId: e.target.value })}
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-gray-600 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      } border focus:ring-green-500 focus:border-green-500`}
                  >
                    <option value="">{t('all_buildings')}</option>
                    {uniqueBuildings.map(building => (
                      <option key={building} value={building}>
                        {building}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('from_date')}
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-gray-600 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      } border focus:ring-green-500 focus:border-green-500`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('to_date')}
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-gray-600 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      } border focus:ring-green-500 focus:border-green-500`}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg transition-all duration-300 transform hover:scale-102 ${isDarkMode ? 'bg-gradient-to-br from-gray-700 to-gray-800 shadow-lg' : 'bg-gradient-to-br from-green-50 to-emerald-100 shadow'
              }`}>
              <div className={`flex items-center `}>
                <div className={`p-3 rounded-full me-4 ${isDarkMode ? 'bg-green-600 bg-opacity-30' : 'bg-green-500 bg-opacity-20'
                  }`}>
                  <RiMoneyDollarCircleLine className={`text-2xl ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('total_payments')}
                  </p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(totalAmount)}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {payments.length} {payments.length === 1 ? t('payment') : t('payments')} {t('recorded')}
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-lg transition-all duration-300 transform hover:scale-102 ${isDarkMode ? 'bg-gradient-to-br from-gray-700 to-gray-800 shadow-lg' : 'bg-gradient-to-br from-red-50 to-red-100 shadow'
              }`}>
              <div className={`flex items-center `}>
                <div className={`p-3 rounded-full me-4 ${isDarkMode ? 'bg-red-600 bg-opacity-30' : 'bg-zinc-500 bg-opacity-20'
                  }`}>
                  <RiMoneyDollarCircleLine className={`text-2xl ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('rent_payments')}
                  </p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(rentTotal)}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {rentPayments.length} {rentPayments.length === 1 ? t('payment') : t('payments')}
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-lg transition-all duration-300 transform hover:scale-102 ${isDarkMode ? 'bg-gradient-to-br from-gray-700 to-gray-800 shadow-lg' : 'bg-gradient-to-br from-purple-50 to-fuchsia-100 shadow'
              }`}>
              <div className={`flex items-center `}>
                <div className={`p-3 rounded-full me-4 ${isDarkMode ? 'bg-purple-600 bg-opacity-30' : 'bg-purple-500 bg-opacity-20'
                  }`}>
                  <RiMoneyDollarCircleLine className={`text-2xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('service_payments')}
                  </p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(serviceTotal)}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {servicePayments.length} {servicePayments.length === 1 ? t('payment') : t('payments')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Expanded Stats */}
          {showStats && !loading && payments.length > 0 && (
            <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('payment_statistics')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Stats by Month */}
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} shadow`}>
                  <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('payments_by_month')}
                  </h4>

                  <div className="space-y-2">
                    {Object.entries(paymentsByMonth).sort((a, b) => {
                      const [monthYearA] = a;
                      const [monthYearB] = b;
                      const [monthA, yearA] = monthYearA.split('/');
                      const [monthB, yearB] = monthYearB.split('/');
                      return yearB - yearA || monthB - monthA;
                    }).slice(0, 5).map(([monthYear, data]) => (
                      <div key={monthYear} className={`flex justify-between items-center `}>
                        <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                          {monthYear}
                        </span>
                        <div className={`flex items-center `}>
                          <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {formatCurrency(data.amount)}
                          </span>
                          <span className={`ms-2 text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                            ({data.count} {t('payments')})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Types Distribution */}
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} shadow`}>
                  <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('payment_types_distribution')}
                  </h4>

                  <div className="space-y-3">
                    {PAYMENT_TYPE_OPTIONS.map(option => {
                      const typePayments = payments.filter(p => p.paymentType === option.value);
                      const typeTotal = typePayments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
                      const percentage = payments.length > 0 ? (typePayments.length / payments.length) * 100 : 0;

                      return (
                        <div key={option.value} className="space-y-1">
                          <div className={`flex justify-between items-center `}>
                            <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                              {t(`payment_type_${option.value}`)}
                            </span>
                            <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {formatCurrency(typeTotal)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getPaymentTypeColor(option.value, isDarkMode).replace('text-', 'bg-').replace('bg-opacity-20', '')}`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-end text-gray-500">
                            {typePayments.length} {t('payments')} ({percentage.toFixed(1)}%)
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Recent Activity */}
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} shadow`}>
                  <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('recent_activity')}
                  </h4>

                  <div className="space-y-3">
                    {payments.slice(0, 5).map(payment => (
                      <div key={payment.id} className={`flex items-center `}>
                        <div className={`h-8 w-8 rounded-full ${payment.paymentType === 'rent'
                          ? 'bg-zinc-500'
                          : payment.paymentType === 'service'
                            ? 'bg-green-500'
                            : 'bg-purple-500'
                          } flex items-center justify-center me-3`}>
                          <RiMoneyDollarCircleLine className="text-white text-sm" />
                        </div>
                        <div className="flex-1 min-w-0 text-start">
                          <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {payment.tenantName}
                          </p>
                          <p className={`text-xs truncate ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                            {formatPaymentDate(payment.paymentDate)}
                          </p>
                        </div>
                        <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(payment.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowStats(false)}
                  className={`px-4 py-2 rounded-lg ${isDarkMode
                    ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    } inline-flex items-center`}
                >
                  <RiCloseLine className="mr-2" />
                  {t('close_statistics')}
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative w-20 h-20">
                <div className={`absolute top-0 left-0 w-full h-full rounded-full border-4 opacity-20 ${isDarkMode ? 'border-red-400' : 'border-red-500'}`}></div>
                <div className={`absolute top-0 left-0 w-full h-full rounded-full border-4 border-transparent border-t-4 ${isDarkMode ? 'border-t-red-400' : 'border-t-red-500'
                  } animate-spin`}></div>
              </div>
              <p className={`mt-6 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('loading_payments')}
              </p>
              <p className={`mt-1 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                {t('this_may_take_moment')}
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-50 text-red-700 border border-red-200'
              } flex flex-col items-center justify-center text-center`}>
              <div className={`p-4 rounded-full ${isDarkMode ? 'bg-red-900 bg-opacity-30' : 'bg-zinc-100'
                } mb-4`}>
                <RiAlertLine className="text-3xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('error_loading_payments')}</h3>
              <p className="mb-4">{error}</p>
              <button
                onClick={handleRefresh}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-700 text-white hover:bg-red-600' : 'bg-red-600 text-white hover:bg-red-700'
                  } flex items-center`}
              >
                <RiRefreshLine className="mr-2" />
                {t('try_again')}
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && payments.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className={`p-6 rounded-full ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-100'
                } mb-4`}>
                <RiMoneyDollarCircleLine className={`text-5xl ${isDarkMode ? 'text-gray-500' : 'text-zinc-400'}`} />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('no_payments_found')}
              </h3>
              <p className={`text-sm mb-6 max-w-md text-center ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                {activeFilters > 0
                  ? t('no_payments_match_filters')
                  : t('no_payments_in_system')}
              </p>

              {activeFilters > 0 ? (
                <button
                  onClick={resetFilters}
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                    } flex items-center mb-2`}
                >
                  <RiFilterOffLine className="mr-2" />
                  {t('reset_filters')}
                </button>
              ) : (
                <Link
                  to="/admin/payments/add"
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
                    } flex items-center mb-2`}
                >
                  <RiAddLine className="mr-2" />
                  {t('record_first_payment')}
                </Link>
              )}
            </div>
          )}

          {/* Payments List */}
          {!loading && !error && filteredPayments.length > 0 && (
            <div className="space-y-4">
              {currentItems.map((payment) => (
                <div
                  key={payment.id}
                  className={`p-4 rounded-lg border ${isDarkMode
                    ? 'bg-gray-750 border-gray-600 hover:bg-zinc-800'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                    } transition-all duration-200 transform hover:shadow-md`}
                >
                  <div className={`flex flex-col lg:flex-row lg:items-center justify-between gap-4 `}>
                    {/* Payment Main Info */}
                    <div className="flex-1">
                      <div className={`flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                        <div className="flex items-center">

                          <div className="text-start">
                            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {formatCurrency(payment.amount)}
                            </h3>
                            <div className={`flex items-center text-sm text-gray-500 mt-1 `}>
                              <RiUserLine className={isRTL ? 'ml-1' : 'mr-1'} size={14} />
                              <span className="truncate max-w-[200px]">
                                {payment.tenantName}
                                {payment.apartmentNumber && (
                                  <span className={`ms-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                    - {t('apt')} {payment.apartmentNumber}
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className={`flex flex-wrap gap-2 mt-2 sm:mt-0 ${isRTL ? 'justify-end' : ''}`}>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentTypeColor(payment.paymentType, isDarkMode)}`}>
                            {t(`payment_type_${payment.paymentType}`) || t('unknown_type')}
                          </span>
                          {payment.buildingName && (
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${isDarkMode ? 'bg-zinc-800 text-gray-300' : 'bg-gray-100 text-gray-800'
                              }`}>
                              <RiBuildingLine className={isRTL ? 'ml-1' : 'mr-1'} size={12} />
                              {payment.buildingName}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Payment Details Row */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className={`flex items-center text-sm `}>
                          <RiCalendarLine className={`me-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={16} />
                          <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {formatPaymentDate(payment.paymentDate)}
                          </span>
                        </div>

                        <div className={`flex items-center text-sm `}>
                          <RiCalendarLine className={`me-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={16} />
                          <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} truncate`}>
                            {formatPaymentPeriod(payment)}
                          </span>
                        </div>

                        {payment.notes && (
                          <div className={`flex items-center text-sm col-span-2 md:col-span-1 `}>
                            <RiFileTextLine className={`me-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={16} />
                            <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} truncate`}>
                              {payment.notes}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className={`flex items-center space-x-2`}>
                      <button
                        onClick={() => handlePrintReceipt(payment)}
                        className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-red-400 hover:bg-gray-600' : 'bg-zinc-50 text-red-600 hover:bg-zinc-100'
                          } transition-colors`}
                        title={t('print_receipt')}
                      >
                        <RiPrinterLine size={18} />
                      </button>

                      <Link
                        to={`/admin/payments/${payment.id}`}
                        className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-red-400 hover:bg-gray-600' : 'bg-zinc-50 text-red-600 hover:bg-zinc-100'
                          } transition-colors`}
                        title={t('view_details')}
                      >
                        <RiEyeLine size={18} />
                      </Link>

                      <Link
                        to={`/admin/payments/${payment.id}/edit`}
                        className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-green-400 hover:bg-gray-600' : 'bg-green-50 text-green-600 hover:bg-green-100'
                          } transition-colors`}
                        title={t('edit_payment')}
                      >
                        <RiPencilLine size={18} />
                      </Link>

                      {(getCurrentUser()?.role === 'admin' || getCurrentUser()?.role === 'front_desk') && (
                        <button
                          onClick={() => openDeleteDialog(payment.id, payment.amount, payment.tenantName)}
                          className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-red-400 hover:bg-gray-600' : 'bg-zinc-50 text-red-600 hover:bg-zinc-100'
                            } transition-colors`}
                          title={t('delete_payment')}
                        >
                          <RiDeleteBin6Line size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={`mt-6 flex justify-between items-center`}>
                  <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                    {t('showing_payments', {
                      start: indexOfFirstItem + 1,
                      end: Math.min(indexOfLastItem, filteredPayments.length),
                      total: filteredPayments.length
                    })}
                  </div>

                  <div className={`flex items-center space-x-2`}>
                    <button
                      onClick={() => paginate(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg ${isDarkMode
                        ? currentPage === 1 ? 'bg-zinc-800 text-gray-500 cursor-not-allowed' : 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
                        : currentPage === 1 ? 'bg-gray-100 text-zinc-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                      <RiArrowRightLine className={`transform rtl:rotate-180`} size={18} />
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      // Create a window of pages centered around current page
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          className={`w-10 h-10 rounded-lg ${currentPage === pageNum
                            ? isDarkMode ? 'bg-red-600 text-white' : 'bg-zinc-500 text-white'
                            : isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-lg ${isDarkMode
                        ? currentPage === totalPages ? 'bg-zinc-800 text-gray-500 cursor-not-allowed' : 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
                        : currentPage === totalPages ? 'bg-gray-100 text-zinc-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                      <RiArrowRightLine className={`transform rtl:rotate-180`} size={18} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Filtered results info */}
          {!loading && !error && payments.length > 0 && filteredPayments.length !== payments.length && filteredPayments.length > 0 && (
            <div className={`mt-4 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'} text-center`}>
              {t('showing_filtered_payments', {
                filtered: filteredPayments.length,
                total: payments.length
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentList;